import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import LoadScreen from '../../../components/load-screen';
import CaseFormForm from '../../../components/cases/forms/CaseFormForm';

import { useLoading } from '../../../hooks';
import { getCase, getCaseForm, updateCaseForm } from '../../../actions/casesActions';
import { getFormById } from '../../../actions/formsActions';
import { getSurgeons } from '../../../actions/surgeonsActions';
import { getHospitals } from '../../../actions/hospitalsActions';
import { getProducts } from '../../../actions/productsActions';
import { getProcedures } from '../../../actions/proceduresActions';
import { getKits } from '../../../actions/kitsActions';
import { getItems } from '../../../actions/itemsActions';

import routes from '../../../constants/routes';
import { formDropdownOptions, formSectionTypes, formStatuses, setPositionOptions } from '../../../constants/enums';
import { nowTimestampUTC } from '../../../utils/date';
import { closeCustomerForm, deleteCustomerForm } from '../../../actions/customerFormsActions';
import userRoles from '../../../constants/userRoles';

const getRequiredDropdowns = (form) => new Set(
  (form?.steps || [])
    .filter((step) => step.type === formSectionTypes.DROPDOWN.value && step.dropdownValues)
    .map((step) => step.dropdownValues)
);

const CaseFormPage = () => {
  const history = useHistory();
  const params = useParams();

  const caseId = params?.id?.replace(':', '');
  const formId = params?.formId?.replace(':', '');

  const dispatch = useDispatch();
  const activeCase = useSelector((state) => state.activeCase.data);
  const userId = useSelector((state) => state.user?.currentUser?.uid);
  const userRole = useSelector((state) => state.user?.currentUser?.role);
  const surgeons = useSelector((state) => state.surgeons.list);
  const hospitals = useSelector((state) => state.hospitals.list);
  const products = useSelector((state) => state.products.list);
  const procedures = useSelector((state) => state.procedures.list);
  const kits = useSelector((state) => state.kits.list);
  const items = useSelector((state) => state.items.list);
  const users = useSelector((state) => state.users.list);

  const [form, setForm] = useState({});
  const [values, setValues] = useState({});
  const [formData, setFormData] = useState({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { loading: fetching, startLoading, stopLoading } = useLoading(false);

  useEffect(() => {
    onLoad().catch((err) => console.error(err));
  }, []);

  const onLoad = async () => {
    startLoading()
    try {
      const [res] = await Promise.all([
        dispatch(getCaseForm(caseId, formId)),
        dispatch(getCase(caseId)),
      ]);

      if (res && res.formId && res.values) {
        setValues(res.values);
        setFormData(res);

        const resForm = await dispatch(getFormById(res.formId));
        if (resForm) {
          setForm(resForm);
          loadFormLookups(resForm).catch((err) => console.error(err));
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const loadFormLookups = async (form) => {
    const dropdowns = getRequiredDropdowns(form);
    const promises = [];

    if (dropdowns.has(formDropdownOptions.SURGEONS.value) && !surgeons.length) {
      promises.push(dispatch(getSurgeons()));
    }
    if (dropdowns.has(formDropdownOptions.HOSPITALS.value) && !hospitals.length) {
      promises.push(dispatch(getHospitals()));
    }
    if (dropdowns.has(formDropdownOptions.PROCEDURES.value) && !procedures.length) {
      promises.push(dispatch(getProcedures()));
    }
    if (dropdowns.has(formDropdownOptions.PRODUCTS.value) && !products.length) {
      promises.push(dispatch(getProducts()));
    }
    if (dropdowns.has(formDropdownOptions.KITS.value) && !kits.length) {
      promises.push(dispatch(getKits()));
    }
    if (dropdowns.has(formDropdownOptions.ITEMS.value) && !items.length) {
      promises.push(dispatch(getItems()));
    }

    await Promise.all(promises);
  };

  const onSubmit = async (files) => {
    try {
      setLoading(true);
      const doc = {
        values,
        status: formStatuses.SUBMITTED,
        submittedAt: nowTimestampUTC(),
        submittedBy: userId,
      };
      await dispatch(updateCaseForm(caseId, formId, doc, files));
      history.push(`${routes.CASES}/${caseId}?view=FORMS`);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (files) => {
    try {
      setSaving(true);
      await dispatch(updateCaseForm(caseId, formId, { values }, files));
      history.push(`${routes.CASES}/${caseId}?view=FORMS`);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    try {
      setDeleting(true);
      await dispatch(deleteCustomerForm(formId));
      history.push(formData?.status === formStatuses.SUBMITTED ? routes.SUBMITTED_FORMS : `${routes.CASES}/${caseId}?view=FORMS`);
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const onClose = async (notes) => {
    try {
      setLoading(true);
      await dispatch(closeCustomerForm(formId, notes));
      history.push(routes.SUBMITTED_FORMS);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onBackClick = () => {
    history.push(`${routes.CASES}/${caseId}?view=FORMS`);
  };

  const formattedId = useMemo(() => {
    if (!activeCase) {
      return ''
    }
    const arr = activeCase?.id?.split('-');
    return arr?.length > 1 ? `${arr[0]}-${arr[1]}` : activeCase?.id;
  }, [activeCase]);

  return (
    <div className='page-container case-checklists-page__container'>
      {fetching && <LoadScreen />}
      {activeCase && activeCase.id && (
        <>
          <div className='d-flex space-between width-100'>
            <div className='page-title'>
              Case
              <span className='page-subtitle'>{formattedId}</span>
            </div>
            {formData?.status === formStatuses.SUBMITTED && (
              <div
                style={{
                  color: setPositionOptions.AVAILABLE.color,
                  fontWeight: 600,
                  border: `2px solid ${setPositionOptions.AVAILABLE.color}`,
                  borderRadius: 8,
                  padding: '2px 12px',
                  background: 'var(--app-surface-raised)'
                }}
              >
                SUBMITTED
              </div>
            )}
            {formData?.status === formStatuses.CLOSED && (
              <div
                style={{
                  color: setPositionOptions.CONSIGNED.color,
                  fontWeight: 600,
                  border: `2px solid ${setPositionOptions.CONSIGNED.color}`,
                  borderRadius: 8,
                  padding: '2px 12px',
                  background: 'var(--app-surface-raised)'
                }}
              >
                CLOSED
              </div>
            )}
          </div>
          {form && !!form?.steps?.length && (
            <CaseFormForm
              activeCase={activeCase}
              form={form}
              values={values}
              setValue={(key, value) => setValues((prevState) => ({ ...prevState, [key]: value }))}
              onSubmit={onSubmit}
              onSave={onSave}
              loading={loading}
              saving={saving}
              surgeons={surgeons}
              hospitals={hospitals}
              procedures={procedures}
              products={products}
              kits={kits}
              items={items}
              status={formData?.status}
              onDelete={onDelete}
              deleting={deleting}
              formData={formData}
              users={users}
              access={form?.roles?.includes(userRole) || userRole === userRoles.ADMIN.name}
              onClose={onClose}
              onBackClick={onBackClick}
              canDeleteSubmittedForm={userRole === userRoles.ADMIN.name}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CaseFormPage;
